import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import {
  Security,
  Warning,
  CheckCircle,
  Schedule,
  TrendingUp,
  Assessment,
} from '@mui/icons-material';
import { LoadingSpinner } from '../components/common';
import { StatsCard, RecentAnalysesTable } from '../components/dashboard';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchDashboardStats, fetchRecentAnalyses } from '../store/slices/dashboardSlice';

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { stats, recentAnalyses, loading, error } = useAppSelector((state) => state.dashboard);

  useEffect(() => {
    dispatch(fetchDashboardStats({}));
    dispatch(fetchRecentAnalyses(10));
  }, [dispatch]);


  if (loading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Overview of counterfeit detection activities and performance metrics
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Analyses"
            value={stats?.total_analyses || 0}
            icon={<Security />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Counterfeits Detected"
            value={stats?.counterfeit_detected || 0}
            icon={<Warning />}
            color="error.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Pending Reviews"
            value={stats?.pending_reviews || 0}
            icon={<Schedule />}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Accuracy Rate"
            value={stats ? `${(stats.accuracy_rate * 100).toFixed(1)}%` : '0%'}
            icon={<CheckCircle />}
            color="success.main"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Products"
            value={stats?.total_products || 0}
            icon={<Assessment />}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Active Brands"
            value={stats?.active_brands || 0}
            icon={<TrendingUp />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="High Risk Items"
            value={stats?.high_risk_items || 0}
            icon={<Warning />}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Avg Processing Time"
            value={stats ? `${stats.avg_processing_time.toFixed(1)}s` : '0s'}
            icon={<Schedule />}
            color="info.main"
          />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Analyses ({recentAnalyses.length})
          </Typography>
          <RecentAnalysesTable analyses={recentAnalyses} />
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard; 